/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 *
 * @file OnboardingSplashPage.jsx
 * Parent-driven onboarding splash screen with v1 / v2 layout variants.
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import React from 'react';
import PropTypes from 'prop-types';
import OnboardingGetStartedPanel from './OnboardingGetStartedPanel';
import OnboardingPromotionPanel from './OnboardingPromotionPanel';
import { OnboardingSplashStylesRoot, classes } from './styles/OnboardingSplashStyles';
import { ONBOARDING_SPLASH_VARIANT } from '../../common/constants';

const OnboardingSplashPage = ({
  className,
  config,
  content,
  onActionClick,
  onLearnMoreClick
}) => {
  const { variant = ONBOARDING_SPLASH_VARIANT.V1 } = config || {};
  const { getStarted, promotion } = content || {};
  const isV2 = variant === ONBOARDING_SPLASH_VARIANT.V2;

  return (
    <OnboardingSplashStylesRoot className={`${classes.shell} ${className}`}>
      <div className={`${classes.layout} ${isV2 ? classes.layoutReverse : ''}`}>
        <div className={classes.panelWide}>
          <OnboardingGetStartedPanel
            title={getStarted.title}
            actionCards={getStarted.actionCards}
            onActionClick={onActionClick}
          />
        </div>
        <div className={classes.panelNarrow}>
          <OnboardingPromotionPanel
            title={promotion.title}
            features={promotion.features}
            learnMoreLabel={promotion.learnMoreLabel}
            onLearnMoreClick={onLearnMoreClick}
          />
        </div>
      </div>
    </OnboardingSplashStylesRoot>
  );
};

OnboardingSplashPage.propTypes = {
  className: PropTypes.string,
  config: PropTypes.shape({
    variant: PropTypes.oneOf([ONBOARDING_SPLASH_VARIANT.V1, ONBOARDING_SPLASH_VARIANT.V2])
  }),
  content: PropTypes.shape({
    getStarted: PropTypes.shape({
      title: PropTypes.string.isRequired,
      actionCards: PropTypes.arrayOf(PropTypes.object).isRequired
    }).isRequired,
    promotion: PropTypes.shape({
      title: PropTypes.string.isRequired,
      features: PropTypes.arrayOf(PropTypes.string).isRequired,
      learnMoreLabel: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  onActionClick: PropTypes.func,
  onLearnMoreClick: PropTypes.func
};

OnboardingSplashPage.defaultProps = {
  className: '',
  config: {
    variant: ONBOARDING_SPLASH_VARIANT.V1
  },
  onActionClick: () => {},
  onLearnMoreClick: () => {}
};

export default OnboardingSplashPage;
